/**
 * cierreTicketEscPos.ts — Genera bytes ESC/POS para imprimir cierres de caja
 * y movimientos (retiro / gasto / ingreso) en la térmica.
 *
 * QUÉ HACE:
 *  - Construye un Uint8Array con comandos ESC/POS listos para enviar a la
 *    impresora térmica configurada (58mm / 80mm) replicando visualmente el
 *    `CierreTicketPreview` y el ticket por defecto del movimiento.
 *
 * QUÉ INVOCA:
 *  - `loadBusinessForReceipt` (saleTicketEscPos) para los datos del negocio.
 *
 * QUÉ NO HACE:
 *  - No conoce de Firebase / mocks / hooks de React. Solo toma input
 *    serializable.
 *
 * REGLAS:
 *  - Mantener consistencia visual con `CierreTicketPreview` y los demás
 *    tickets: separadores con `_`, mismo orden de bloques, mismas etiquetas.
 *  - Latin-1 (sin emojis), centrado y negrita usando comandos ESC/POS.
 */

import type {
  CierreCaja,
  Credito,
  MovimientoCaja,
  Venta,
} from "../types/caja.types";
import { readCreditosSnapshotForSesion } from "./readCreditosSnapshotForSesion";
import { buildCierreTicketPlainText } from "./buildCierreTicketPlainText";
import {
  buildMovimientoTicketPlainText,
  shouldBoldMovimientoTicketLine,
  shouldDoubleMovimientoTicketLine,
  stripMovimientoTicketLineMarkers,
} from "./buildMovimientoTicketPlainText";
import {
  decodeEscPosToPreviewLines,
  encodePlainTextToEscPos,
  plainTextLinesToPreviewLines,
} from "@/features/caja/utils/escPosPlainTextDecode";
import {
  getTicketMaxWidth,
  type ThermalPreviewLine,
} from "@/features/caja/utils/escposLayout";

// ─── Helpers de bytes / encoding (copiados sin re-export para mantener
// el archivo de ventas estable y no tocar su superficie pública). ────────────

const UNICODE_TO_LATIN1: Record<string, number> = {
  "\u00c1": 0xc1,
  "\u00c9": 0xc9,
  "\u00cd": 0xcd,
  "\u00d3": 0xd3,
  "\u00da": 0xda,
  "\u00dc": 0xdc,
  "\u00d1": 0xd1,
  "\u00e1": 0xe1,
  "\u00e9": 0xe9,
  "\u00ed": 0xed,
  "\u00f3": 0xf3,
  "\u00fa": 0xfa,
  "\u00fc": 0xfc,
  "\u00f1": 0xf1,
  "\u00a1": 0xa1,
  "\u00bf": 0xbf,
  "\u00b0": 0xb0,
};

function stripUnsupported(s: string): string {
  return s
    .replace(/[\u{1F300}-\u{1FAFF}]/gu, "")
    .replace(/[\u2600-\u26FF]/g, "")
    .replace(/[\u2010-\u2015\u2212]/g, "-");
}

function u8(...bytes: number[]): Uint8Array {
  return new Uint8Array(bytes);
}

function concat(...parts: Uint8Array[]): Uint8Array {
  const n = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(n);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

function encodeReceiptText(s: string): Uint8Array {
  const cleaned = stripUnsupported(s.replace(/\u20ac/g, "EUR")).normalize(
    "NFC",
  );
  const bytes: number[] = [];
  for (const ch of cleaned) {
    const code = ch.codePointAt(0) ?? 0;
    if (code <= 0xff) {
      bytes.push(code);
      continue;
    }
    const mapped = UNICODE_TO_LATIN1[ch];
    if (mapped !== undefined) bytes.push(mapped);
    else bytes.push(0x3f);
  }
  return new Uint8Array(bytes);
}

function lineLatin1(s: string): Uint8Array {
  return concat(encodeReceiptText(s), u8(0x0a));
}

// ─── CIERRE ─────────────────────────────────────────────────────────────────

export interface CierreEscPosInput {
  cierre: CierreCaja;
  ventas: Venta[];
  paperWidthMm: 58 | 80;
  variant?: "cierre" | "parcial";
}

function resolveCreditosSesionForEscPos(cierre: CierreCaja): Credito[] {
  if (Array.isArray(cierre.creditosSnapshot)) {
    return [...cierre.creditosSnapshot].sort(
      (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime(),
    );
  }
  return readCreditosSnapshotForSesion(cierre.sesionId).sort(
    (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime(),
  );
}

function buildCierrePlainTextFromInput(input: CierreEscPosInput): string[] {
  return buildCierreTicketPlainText({
    cierre: input.cierre,
    ventas: input.ventas,
    creditosSesion: resolveCreditosSesionForEscPos(input.cierre),
    paperWidthMm: input.paperWidthMm,
    variant: input.variant,
  });
}

/** Bytes ESC/POS del cierre — texto canónico + encode (preview = impresión). */
export function buildCierreEscPos(input: CierreEscPosInput): Uint8Array {
  const cols = getTicketMaxWidth(input.paperWidthMm);
  const lines = buildCierrePlainTextFromInput(input);
  return encodePlainTextToEscPos(lines, cols, lineLatin1, { includeCut: true });
}

/** Preview térmico 1:1 con bytes ESC/POS (misma tubería que venta/movimiento). */
export function buildCierrePreviewLines(
  input: CierreEscPosInput,
): ThermalPreviewLine[] {
  const cols = getTicketMaxWidth(input.paperWidthMm);
  const bytes = buildCierreEscPos(input);
  const cutLen = 4;
  const withoutCut =
    bytes.length > cutLen ? bytes.subarray(0, bytes.length - cutLen) : bytes;
  return decodeEscPosToPreviewLines(withoutCut, cols);
}

/** Líneas de texto plano del comprobante térmico canónico. */
export function buildCierrePlainTextLines(input: CierreEscPosInput): string[] {
  return buildCierrePlainTextFromInput(input);
}

// ─── MOVIMIENTO (retiro / gasto / ingreso) ──────────────────────────────────

export interface MovimientoEscPosInput {
  movimiento: MovimientoCaja;
  paperWidthMm: 58 | 80;
  ventas?: Venta[];
  creditos?: Credito[];
}

export function buildMovimientoEscPos(
  input: MovimientoEscPosInput,
): Uint8Array {
  const cols = getTicketMaxWidth(input.paperWidthMm);
  const lines = buildMovimientoTicketPlainText({
    movimiento: input.movimiento,
    ventas: input.ventas,
    creditos: input.creditos,
    paperWidthMm: input.paperWidthMm,
  });
  return encodePlainTextToEscPos(lines, cols, lineLatin1, {
    shouldBold: shouldBoldMovimientoTicketLine,
    shouldDouble: (line) => shouldDoubleMovimientoTicketLine(line),
    stripLine: stripMovimientoTicketLineMarkers,
    includeCut: true,
  });
}

/** Preview térmico 1:1 con texto plano canónico del movimiento. */
export function buildMovimientoPreviewLines(
  input: MovimientoEscPosInput,
): ThermalPreviewLine[] {
  const cols = getTicketMaxWidth(input.paperWidthMm);
  const lines = buildMovimientoTicketPlainText({
    movimiento: input.movimiento,
    ventas: input.ventas,
    creditos: input.creditos,
    paperWidthMm: input.paperWidthMm,
  });
  return plainTextLinesToPreviewLines(
    lines,
    cols,
    shouldBoldMovimientoTicketLine,
  );
}

/** Líneas de texto plano idénticas a las que imprime la APK (movimiento de caja). */
export function buildMovimientoPlainTextLines(
  input: MovimientoEscPosInput,
): string[] {
  return buildMovimientoPreviewLines(input).map((ln) => ln.text);
}
